import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Copy, MessageCircle, Send, CheckCircle2, Clock, Package, FileText, Download, RefreshCw, ThumbsUp } from "lucide-react";
import { api, API_BASE as API } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { DashboardHeader } from "@/components/DashboardHeader";

const statusStyle = {
  sent: "bg-blue-100 text-blue-800",
  accepted: "bg-teal-100 text-teal-800",
  advance_paid: "bg-amber-100 text-amber-800",
  service_completed: "bg-purple-100 text-purple-800",
  final_paid: "bg-green-100 text-green-800",
  cancelled: "bg-slate-200 text-slate-700",
};

const statusLabel = (s) =>
  ({ sent: "Sent", accepted: "Accepted", advance_paid: "Advance paid", service_completed: "Service complete", final_paid: "Paid in full", cancelled: "Cancelled" }[s] || s);

const emptyForm = {
  quote_type: "hourly",
  service_type: "Man & Van",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  service_description: "",
  base_rate: "35",
  base_hours: "2",
  extra_hour_rate: "20",
  advance_percent: "100",
  total_pounds: "",
  advance_pounds: "",
  is_direct_invoice: false,
  customer_address: "",
  actual_hours: "",
  booking_date: "",
  booking_time: "",
  notes: "",
};

export const AdminQuotes = () => {
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isHourly = form.quote_type === "hourly";
  const isDirect = form.is_direct_invoice;
  const baseRate = parseFloat(form.base_rate) || 0;
  const baseHours = parseFloat(form.base_hours) || 0;
  const extraHourRate = parseFloat(form.extra_hour_rate) || 0;
  const advancePct = Math.max(0, Math.min(100, parseFloat(form.advance_percent) || 0));
  const baseTotal = +(baseRate * baseHours).toFixed(2);
  const advanceAmount = isHourly ? +(baseTotal * advancePct / 100).toFixed(2) : 0;

  const create = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.service_description) {
      toast.error("Fill name, email, service description"); return;
    }
    if (isDirect && !form.customer_address.trim()) {
      toast.error("Address is required for invoices"); return;
    }
    let payload = {
      customer_name: form.customer_name,
      customer_email: form.customer_email,
      customer_phone: form.customer_phone,
      service_description: form.service_description,
      notes: form.notes,
      quote_type: form.quote_type,
      is_direct_invoice: isDirect,
      customer_address: form.customer_address,
      booking_date: form.booking_date,
      booking_time: form.booking_time,
    };
    if (isHourly) {
      if (!(baseRate > 0) || !(baseHours > 0) || extraHourRate < 0) {
        toast.error("Base rate, base hours must be > 0"); return;
      }
      if (advancePct < 0 || advancePct > 100) {
        toast.error("Advance % must be between 0 and 100"); return;
      }
      payload = { ...payload,
        service_type: form.service_type,
        base_rate: baseRate,
        base_hours: baseHours,
        extra_hour_rate: extraHourRate,
        advance_percent: advancePct,
      };
      if (isDirect) {
        const extra = parseFloat(form.actual_hours) || 0;
        if (extra < 0) { toast.error("Additional hours must be ≥ 0"); return; }
        payload.actual_hours = baseHours + extra;  // backend expects total actual_hours
      }
    } else {
      const total = parseFloat(form.total_pounds);
      if (!(total > 0)) { toast.error("Enter total"); return; }
      payload.total_pounds = total;
      if (!isDirect) {
        const advance = parseFloat(form.advance_pounds);
        if (!(advance > 0)) { toast.error("Enter advance"); return; }
        if (advance > total) { toast.error("Advance cannot exceed total"); return; }
        payload.advance_pounds = advance;
      }
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/quotes", payload);
      toast.success(isDirect ? "Invoice created and emailed" : "Quote created and emailed to customer");
      copyToClipboard(data.public_url, "Link copied");
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally { setCreating(false); }
  };

  const copyToClipboard = (text, label) => {
    if (!navigator?.clipboard?.writeText) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(label || "Copied"),
      () => {}  // silently ignore in non-secure contexts / missing permissions
    );
  };

  const filtered = quotes.filter((q) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (q.customer_name || "").toLowerCase().includes(s) ||
           (q.customer_email || "").toLowerCase().includes(s) ||
           (q.customer_phone || "").toLowerCase().includes(s) ||
           (q.invoice_number || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="admin-quotes" className="min-h-screen bg-[#F8FAFC]">
      <Seo title="Admin · Quotes" description="Admin quote management" canonical="/boxbquote" noindex />
      <DashboardHeader title="Quotes & Invoices" subtitle="Send quotes, mark accepted, collect payments, generate invoices." />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 pb-16 space-y-8">
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to="/boxbquote" className="rounded-full bg-[#0A192F] text-white px-4 py-1.5 font-semibold">Quotes</Link>
          <Link to="/boxbquote/customers" data-testid="nav-customers" className="rounded-full bg-white border border-slate-200 hover:border-[#0A192F] px-4 py-1.5 font-semibold text-[#0A192F]">Customers</Link>
          <Link to="/boxbquote/invoices" data-testid="nav-invoices" className="rounded-full bg-white border border-slate-200 hover:border-[#0A192F] px-4 py-1.5 font-semibold text-[#0A192F]">Invoices</Link>
          <Link to="/boxbquote/enquiries" className="rounded-full bg-white border border-slate-200 hover:border-[#0A192F] px-4 py-1.5 font-semibold text-[#0A192F]">Enquiries</Link>
        </div>

        <form onSubmit={create} data-testid="quote-create-form" className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-display font-semibold text-2xl text-[#0A192F]">Create new {isDirect ? "invoice" : "quote"}</h2>
            <label className="inline-flex items-center gap-2 rounded-full bg-[#F8FAFC] border border-slate-200 px-4 py-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="direct-invoice-toggle" checked={isDirect}
                     onChange={(e) => setForm({...form, is_direct_invoice: e.target.checked})}
                     className="accent-[#0A192F]" />
              <span className="font-semibold text-[#0A192F]">Direct invoice</span>
            </label>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {isDirect
              ? "Skip the quote step — customer gets an invoice they can pay in full immediately (used when you quoted over phone)."
              : "Customer receives a quote, pays advance to book, and you generate the invoice on completion."}
          </p>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <TypeTab active={isHourly} onClick={() => setForm({ ...form, quote_type: "hourly" })}
                     icon={Clock} title="Hourly" sub="Man & Van / Cleaning — base rate + extra hourly" tid="type-hourly" />
            <TypeTab active={!isHourly} onClick={() => setForm({ ...form, quote_type: "fixed" })}
                     icon={Package} title="Fixed" sub="Flat total & advance you set manually" tid="type-fixed" />
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <QInput label="Customer name*" v={form.customer_name} on={(v) => setForm({...form, customer_name: v})} tid="q-name" />
            <QInput label="Email*" type="email" v={form.customer_email} on={(v) => setForm({...form, customer_email: v})} tid="q-email" />
            <QInput label="Phone (for WhatsApp)" v={form.customer_phone} on={(v) => setForm({...form, customer_phone: v})} tid="q-phone" ph="+44..." />

            {isHourly ? (
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Service type*</label>
                <select data-testid="q-service-type" value={form.service_type}
                        onChange={(e) => setForm({...form, service_type: e.target.value})}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]">
                  <option>Man &amp; Van</option>
                  <option>Cleaning</option>
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <QInput label="Total £*" type="number" v={form.total_pounds} on={(v) => setForm({...form, total_pounds: v})} tid="q-total" />
                {!isDirect && <QInput label="Advance £*" type="number" v={form.advance_pounds} on={(v) => setForm({...form, advance_pounds: v})} tid="q-advance" />}
              </div>
            )}
          </div>

          {isHourly && (
            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              <QInput label="Base rate £/hr*" type="number" v={form.base_rate} on={(v) => setForm({...form, base_rate: v})} tid="q-base-rate" ph="e.g. 17.50" />
              <QInput label="Base hours*" type="number" v={form.base_hours} on={(v) => setForm({...form, base_hours: v})} tid="q-base-hours" ph="e.g. 2" />
              <QInput label="Extra hour rate £/hr*" type="number" v={form.extra_hour_rate} on={(v) => setForm({...form, extra_hour_rate: v})} tid="q-extra-rate" ph="e.g. 20" />
            </div>
          )}

          {isHourly && !isDirect && (
            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Advance % of base total*</label>
                <div className="flex items-center gap-2">
                  <input data-testid="q-advance-percent" type="number" min="0" max="100" step="5"
                         value={form.advance_percent} onChange={(e) => setForm({...form, advance_percent: e.target.value})}
                         className="w-24 rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]" />
                  <span className="text-slate-500 text-sm">% (100 = full base upfront)</span>
                </div>
                <div className="mt-2 flex gap-1">
                  {[30, 50, 75, 100].map((p) => (
                    <button key={p} type="button" data-testid={`q-advance-preset-${p}`}
                            onClick={() => setForm({...form, advance_percent: String(p)})}
                            className={`text-xs px-3 py-1 rounded-full ${parseFloat(form.advance_percent) === p ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {p}%
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2 flex items-end">
                <div className="w-full rounded-2xl bg-[#F8FAFC] border border-slate-200 p-4 text-sm">
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Customer pays upfront</div>
                  <div className="mt-1 font-display font-bold text-2xl text-[#0A192F]">£{advanceAmount.toFixed(2)}</div>
                  <div className="text-xs text-slate-500 mt-1">Base total £{baseTotal.toFixed(2)} (£{baseRate.toFixed(2)}/hr × {baseHours}hrs) · Balance £{(baseTotal - advanceAmount).toFixed(2)} + extras billed on completion.</div>
                </div>
              </div>
            </div>
          )}

          {isDirect && isHourly && (
            <div className="mt-4">
              <QInput label="Additional hours to bill*" type="number" v={form.actual_hours} on={(v) => setForm({...form, actual_hours: v})} tid="q-additional-hours" ph="e.g. 1 (extras beyond base)" />
              <p className="mt-1 text-xs text-slate-500">
                Base {baseHours} hrs already covered. Enter extras only (0 if job finished within base).
              </p>
            </div>
          )}

          {isDirect && (
            <div className="mt-4">
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Customer address* (shown on invoice)</label>
              <textarea data-testid="q-address" rows={2} value={form.customer_address}
                        onChange={(e) => setForm({...form, customer_address: e.target.value})}
                        placeholder="e.g. 12 Cathedral Rd, Cardiff CF11 9LJ"
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]" />
            </div>
          )}

          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Booking date</label>
              <input data-testid="q-booking-date" type="date" value={form.booking_date}
                     onChange={(e) => setForm({...form, booking_date: e.target.value})}
                     className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Start time</label>
              <input data-testid="q-booking-time" type="time" value={form.booking_time}
                     onChange={(e) => setForm({...form, booking_time: e.target.value})}
                     className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]" />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Service description*</label>
            <textarea data-testid="q-service" rows={3} value={form.service_description}
                      onChange={(e) => setForm({...form, service_description: e.target.value})}
                      placeholder={isHourly ? "e.g. Man & Van — Cardiff → Penarth, 1x sofa, 6 boxes." : "e.g. 3-bed house removal Cardiff → Penarth."}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]" />
          </div>

          {isHourly && baseRate > 0 && baseHours > 0 && (
            <div data-testid="hourly-preview" className="mt-4 rounded-2xl bg-[#F8FAFC] border border-slate-200 p-4 text-sm text-slate-700">
              <div className="font-semibold text-[#0A192F]">Customer will see:</div>
              <div className="mt-1">
                <b>£{baseRate.toFixed(2)}/hr</b> × {baseHours}{baseHours === 1 ? " hr" : " hrs"} = <b>£{baseTotal.toFixed(2)}</b> base
                {extraHourRate > 0 && <> · <b>£{extraHourRate.toFixed(2)}/hr</b> thereafter</>}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {isDirect
                  ? <>Invoice total: <b>£{(baseTotal + Math.max(0, parseFloat(form.actual_hours) || 0) * extraHourRate).toFixed(2)}</b> · payable in full.</>
                  : <>Advance charged upfront: <b>£{advanceAmount.toFixed(2)}</b> ({advancePct}% of £{baseTotal.toFixed(2)} base). Balance + extras billed on completion.</>}
              </div>
              {form.booking_date && (
                <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs">
                  <span className="text-slate-500">Booking:</span>
                  <b className="text-[#0A192F]">{new Date(form.booking_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}{form.booking_time ? ` at ${form.booking_time}` : ""}</b>
                </div>
              )}
            </div>
          )}

          <div className="mt-4">
            <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Internal notes (not shown to customer)</label>
            <textarea data-testid="q-notes" rows={2} value={form.notes}
                      onChange={(e) => setForm({...form, notes: e.target.value})}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]" />
          </div>
          <button type="submit" disabled={creating} data-testid="q-submit"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-8 py-3.5 font-semibold hover:bg-[#0F172A] disabled:opacity-60">
            {creating ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</> : <><Send className="h-4 w-4" /> Create &amp; send {isDirect ? "invoice" : "quote"}</>}
          </button>
        </form>

        <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex flex-wrap items-center gap-3 justify-between">
            <h2 className="font-display font-semibold text-xl text-[#0A192F]">All quotes</h2>
            <input data-testid="quotes-search" type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search name, email, phone, invoice #"
                   className="rounded-full border border-slate-200 px-4 py-2 text-sm w-full sm:w-72 outline-none focus:border-[#0A192F]" />
          </div>
          {loading ? <div className="p-10 text-center text-slate-500">Loading…</div>
          : filtered.length === 0 ? <div className="p-10 text-center text-slate-500" data-testid="admin-quotes-empty">{search ? "No matches." : "No quotes yet. Create one above."}</div>
          : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((q) => (
                <li key={q.id} data-testid={`admin-quote-${q.id}`} className="p-5 grid md:grid-cols-12 gap-3 items-center">
                  <div className="md:col-span-4 min-w-0">
                    <div className="font-display font-semibold text-[#0A192F] truncate">{q.customer_name}</div>
                    <div className="text-xs text-slate-500 truncate">{q.customer_email}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-widest">
                      {q.quote_type === "hourly" && <span className="inline-flex items-center gap-1 text-[#2563EB]"><Clock className="h-3 w-3" /> Hourly · {q.service_type || "—"}</span>}
                      {q.is_invoice && <span className="inline-flex items-center gap-1 text-purple-700"><FileText className="h-3 w-3" /> {q.invoice_number}</span>}
                    </div>
                  </div>
                  <div className="md:col-span-3 text-sm">
                    <div className="text-slate-500 text-xs uppercase tracking-widest font-bold">Total</div>
                    <div className="text-[#0A192F] font-semibold">£{Number(q.total_pounds).toFixed(2)}</div>
                    <div className="text-slate-500 text-xs">Advance £{Number(q.advance_pounds).toFixed(2)}</div>
                  </div>
                  <div className="md:col-span-3">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusStyle[q.status] || "bg-slate-100 text-slate-700"}`}>
                      {statusLabel(q.status)}
                    </span>
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <button onClick={() => nav(`/boxbquote/quotes/${q.id}`)} data-testid={`open-quote-${q.id}`}
                            className="rounded-full bg-slate-100 hover:bg-slate-200 px-4 py-1.5 text-sm font-semibold text-[#0A192F]">Open</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const QInput = ({ label, v, on, tid, type = "text", ph }) => (
  <div>
    <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">{label}</label>
    <input data-testid={tid} type={type} value={v} onChange={(e) => on(e.target.value)} placeholder={ph}
           className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]" />
  </div>
);

const TypeTab = ({ active, onClick, icon: Icon, title, sub, tid }) => (
  <button type="button" onClick={onClick} data-testid={tid}
          className={`text-left rounded-2xl border p-4 flex items-start gap-3 transition ${active ? "border-[#0A192F] bg-[#0A192F] text-white" : "border-slate-200 bg-white text-[#0A192F] hover:border-[#0A192F]"}`}>
    <Icon className="h-5 w-5 mt-0.5 shrink-0" />
    <div>
      <div className="font-semibold">{title}</div>
      <div className={`text-xs mt-0.5 ${active ? "text-white/70" : "text-slate-500"}`}>{sub}</div>
    </div>
  </button>
);

export const AdminQuoteDetail = () => {
  const nav = useNavigate();
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [additionalHours, setAdditionalHours] = useState("");
  const [address, setAddress] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/quotes/${id}`);
      setQ(data);
      setAddress(data.customer_address || "");
    } catch { toast.error("Not found"); nav("/boxbquote"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const copy = (t, l) => navigator.clipboard.writeText(t).then(() => toast.success(l));

  const openComplete = () => {
    setAdditionalHours("0");
    setAddress(q.customer_address || "");
    setShowModal(true);
  };

  const submitComplete = async () => {
    if (!address.trim()) { toast.error("Address is required"); return; }
    const body = { customer_address: address };
    if (q.quote_type === "hourly") {
      const extra = parseFloat(additionalHours);
      if (!(extra >= 0)) { toast.error("Enter additional hours (0 or more)"); return; }
      body.additional_hours = extra;
    }
    setBusy(true);
    try {
      await api.post(`/admin/quotes/${id}/mark-complete`, body);
      toast.success("Invoice generated & emailed");
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const acceptQuote = async () => {
    setBusy(true);
    try { await api.post(`/admin/quotes/${id}/accept`); toast.success("Marked accepted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const resendInvoice = async () => {
    setBusy(true);
    try { await api.post(`/admin/quotes/${id}/resend-invoice`); toast.success("Invoice re-sent to customer"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!q) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const remaining = Number(q.total_pounds) - Number(q.advance_pounds);
  const isHourly = q.quote_type === "hourly";
  const previewExtra = isHourly ? Math.max(0, parseFloat(additionalHours) || 0) : 0;
  const previewBaseTotal = isHourly ? +(Number(q.base_rate || 0) * Number(q.base_hours || 0)).toFixed(2) : 0;
  const previewExtraTotal = isHourly ? +(previewExtra * Number(q.extra_hour_rate || 0)).toFixed(2) : 0;
  const previewTotal = isHourly ? +(previewBaseTotal + previewExtraTotal).toFixed(2) : 0;
  const previewBalance = isHourly ? +(previewTotal - Number(q.advance_pounds || 0)).toFixed(2) : 0;
  const pdfHref = `${API}/admin/quotes/${q.id}/invoice.pdf`;

  return (
    <div data-testid="admin-quote-detail" className="min-h-screen bg-[#F8FAFC]">
      <Seo title="Admin · Quote detail" description="" canonical="/boxbquote" noindex />
      <DashboardHeader title={q.customer_name} subtitle={statusLabel(q.status) + (q.invoice_number ? ` · ${q.invoice_number}` : "")} />
      <div className="max-w-4xl mx-auto px-6 lg:px-8 pb-16 space-y-6">
        <div className="rounded-3xl bg-white border border-slate-200 p-6">
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <Field k="Email" v={q.customer_email} />
            <Field k="Phone" v={q.customer_phone || "—"} />
            <Field k="Status" v={statusLabel(q.status)} />
            <Field k="Type" v={isHourly ? `Hourly · ${q.service_type || "—"}` : "Fixed"} />
            <Field k="Total" v={`£${Number(q.total_pounds).toFixed(2)}`} />
            <Field k="Advance" v={`£${Number(q.advance_pounds).toFixed(2)}`} />
            <Field k="Remaining" v={`£${remaining.toFixed(2)}`} />
            {isHourly && <Field k="Base" v={`£${Number(q.base_rate).toFixed(2)}/hr × ${q.base_hours}hrs = £${(Number(q.base_rate) * Number(q.base_hours)).toFixed(2)}`} />}
            {isHourly && <Field k="Extra hour" v={`£${Number(q.extra_hour_rate).toFixed(2)}/hr`} />}
            {isHourly && Number(q.advance_percent) !== 100 && <Field k="Advance %" v={`${Number(q.advance_percent)}%`} />}
            {isHourly && q.actual_hours != null && <Field k="Actual hours" v={`${q.actual_hours} hrs`} />}
            {q.booking_date && <Field k="Booking" v={`${q.booking_date}${q.booking_time ? ' ' + q.booking_time : ''}`} />}
            {q.invoice_number && <Field k="Invoice #" v={q.invoice_number} />}
            {q.customer_address && <div className="sm:col-span-3"><Field k="Address" v={q.customer_address} /></div>}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs text-slate-500 uppercase tracking-widest font-bold">Service</div>
            <p className="mt-1 text-[#0A192F]">{q.service_description}</p>
          </div>
          {q.notes && <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs text-slate-500 uppercase tracking-widest font-bold">Internal notes</div>
            <p className="mt-1 text-slate-600 text-sm">{q.notes}</p>
          </div>}
        </div>

        <div className="rounded-3xl bg-white border border-slate-200 p-6">
          <h3 className="font-display font-semibold text-lg text-[#0A192F]">Share with customer</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="copy-link-btn" onClick={() => copy(q.public_url, "Link copied")}
                    className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#0F172A]">
              <Copy className="h-4 w-4" /> Copy link
            </button>
            <a href={q.whatsapp_share} target="_blank" rel="noopener noreferrer" data-testid="wa-share-btn"
               className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-2.5 text-sm font-semibold hover:brightness-95">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            {q.is_invoice && (
              <>
                <a href={pdfHref} target="_blank" rel="noopener noreferrer" data-testid="download-pdf-btn"
                   className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 hover:border-[#0A192F] text-[#0A192F] px-5 py-2.5 text-sm font-semibold">
                  <Download className="h-4 w-4" /> Download PDF
                </a>
                <button onClick={resendInvoice} disabled={busy} data-testid="resend-invoice-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 hover:border-[#0A192F] text-[#0A192F] px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
                  <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Resend invoice
                </button>
              </>
            )}
          </div>
          <p className="mt-3 text-xs text-slate-500 break-all">{q.public_url}</p>
        </div>

        <div className="rounded-3xl bg-white border border-slate-200 p-6">
          <h3 className="font-display font-semibold text-lg text-[#0A192F]">Actions</h3>
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-3">
              <CheckCircle2 className={`h-5 w-5 ${q.accepted_at ? "text-green-600" : "text-slate-300"}`} />
              <span className="text-sm">Quote accepted {q.accepted_at && `on ${new Date(q.accepted_at).toLocaleString("en-GB")}`}</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className={`h-5 w-5 ${q.advance_paid_at ? "text-green-600" : "text-slate-300"}`} />
              <span className="text-sm">Advance paid {q.advance_paid_at && `on ${new Date(q.advance_paid_at).toLocaleString("en-GB")}`}</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className={`h-5 w-5 ${q.service_completed_at ? "text-green-600" : "text-slate-300"}`} />
              <span className="text-sm">Service complete / invoice issued {q.service_completed_at && `on ${new Date(q.service_completed_at).toLocaleString("en-GB")}`}</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className={`h-5 w-5 ${q.final_paid_at ? "text-green-600" : "text-slate-300"}`} />
              <span className="text-sm">Final payment received {q.final_paid_at && `on ${new Date(q.final_paid_at).toLocaleString("en-GB")}`}</span>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {!q.is_invoice && q.status === "sent" && (
              <button onClick={acceptQuote} disabled={busy} data-testid="accept-quote-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-teal-600 text-white px-6 py-2.5 text-sm font-semibold hover:bg-teal-700 disabled:opacity-60">
                <ThumbsUp className="h-4 w-4" /> Mark quote accepted
              </button>
            )}
            {(q.status === "advance_paid" || q.status === "sent" || q.status === "accepted") && (
              <button onClick={openComplete} disabled={busy} data-testid="mark-complete-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#1E40AF] disabled:opacity-60">
                <FileText className="h-4 w-4" /> Mark complete &amp; generate invoice
              </button>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div data-testid="complete-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 sm:p-8 shadow-xl">
            <h3 className="font-display font-semibold text-xl text-[#0A192F]">Generate invoice</h3>
            {isHourly && (
              <p className="mt-1 text-sm text-slate-500">
                Base: £{Number(q.base_rate).toFixed(2)}/hr × {q.base_hours} hrs · £{Number(q.extra_hour_rate).toFixed(2)}/hr thereafter.
              </p>
            )}
            {isHourly && (
              <div className="mt-4">
                <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Additional hours to bill</label>
                <input data-testid="additional-hours-input" type="number" min="0" step="0.25"
                       value={additionalHours} onChange={(e) => setAdditionalHours(e.target.value)}
                       placeholder="0"
                       className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]" />
                <p className="mt-1 text-xs text-slate-500">
                  Base {q.base_hours} hrs already covered. Enter extras only (0 if job finished within base).
                </p>
              </div>
            )}
            <div className="mt-4">
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Customer address* (shown on invoice)</label>
              <textarea data-testid="address-input" rows={3} value={address} onChange={(e) => setAddress(e.target.value)}
                        placeholder="e.g. 12 Cathedral Rd, Cardiff CF11 9LJ"
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]" />
            </div>
            {isHourly && (
              <div className="mt-4 rounded-2xl bg-[#F8FAFC] border border-slate-200 p-4 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Base ({q.base_hours} hrs × £{Number(q.base_rate).toFixed(2)}/hr)</span><span className="font-semibold">£{previewBaseTotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Extra ({previewExtra.toFixed(2)} hrs × £{Number(q.extra_hour_rate).toFixed(2)}/hr)</span><span className="font-semibold">£{previewExtraTotal.toFixed(2)}</span></div>
                <div className="mt-2 pt-2 border-t border-slate-200 flex justify-between text-base"><span className="font-semibold text-[#0A192F]">Total</span><span className="font-semibold text-[#0A192F]">£{previewTotal.toFixed(2)}</span></div>
                <div className="mt-1 text-xs text-slate-500">Balance to invoice: <b>£{previewBalance.toFixed(2)}</b> (after £{Number(q.advance_pounds).toFixed(2)} advance)</div>
              </div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setShowModal(false)} data-testid="modal-cancel"
                      className="rounded-full bg-slate-100 hover:bg-slate-200 px-5 py-2 text-sm font-semibold text-[#0A192F]">Cancel</button>
              <button onClick={submitComplete} disabled={busy} data-testid="modal-confirm"
                      className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-5 py-2 text-sm font-semibold hover:bg-[#0F172A] disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Generate invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ k, v }) => (
  <div>
    <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">{k}</div>
    <div className="text-sm text-[#0A192F] font-medium whitespace-pre-line">{v}</div>
  </div>
);
